
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      let errorMessage = "Something went wrong. Please try again later.";
      let isFirestoreError = false;

      try {
        if (this.state.error?.message) {
          const parsed = JSON.parse(this.state.error.message);
          if (parsed.authInfo && parsed.operationType) {
            isFirestoreError = true;
            errorMessage = `Permission denied during ${parsed.operationType} on ${parsed.path || 'unknown path'}.`;
          }
        }
      } catch (e) {
        // Not a JSON error message, use default
      }

      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 text-center">
          <div className="max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
            <h2 className="mb-4 text-2xl font-bold text-foreground">Oops!</h2>
            <p className="mb-6 text-muted-foreground">
              {errorMessage}
            </p>
            {isFirestoreError && (
              <p className="mb-6 text-sm text-accent">
                This might be due to missing permissions. If you just signed in, try refreshing.
              </p>
            )}
            <Button onClick={this.handleReset} className="w-full">
              Back to Home
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
